/**
 * HourlyLogPdfCard — downloads a daily hourly power log as PDF.
 * Designed for an ITI/shop-floor engineer: large fonts, bold table, no clutter.
 *
 * Column set: Hour | kWh Accumulated | kWh This Hour | kVAh Accumulated | kVAh This Hour | Power Factor
 *
 * Power Factor per row = kWh Accumulated / kVAh Accumulated (ratio for that hour).
 *
 * "This Hour" columns are shifted up one row: row H shows the energy consumed
 * between hour H and hour H+1 (i.e. the delta recorded at H+1). The underlying
 * cumulative integration is unchanged — only the display row is shifted.
 *
 * No-data hours carry forward the last known accumulated value instead of showing —.
 */

import { useMemo, useState } from 'react';
import {
  Document, Page, View, Text, StyleSheet, PDFDownloadLink,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { Card } from '@/components/ui/Card';
import { FileDown, CalendarDays } from 'lucide-react';
import { powerFactor, isValidPfReading, energyDelivered, apparentEnergyKvah } from '@/lib/calculations';
import type { TelemetryRow } from '@/types';

/* -------------------------------------------------------------------------- */
/*  Types & helpers                                                            */
/* -------------------------------------------------------------------------- */

interface HourlyRow {
  hour:      number;
  kwhAcc:    number;
  kvahAcc:   number;
  kwhDelta:  number;
  kvahDelta: number;
  pfAvg:     number;
  count:     number;
}

/**
 * Rolling-cumulative energy logic:
 *
 *   Day N · 00:00  →  0  (energy accumulated from this day's start — nothing yet)
 *   Day N · 01:00  →  kWh delivered 00:00 → 01:00
 *   Day N · 23:00  →  kWh delivered 00:00 → 23:00
 *
 *   Day N+1 · 00:00  →  Day N total (energy from Day N 00:00 through Day N+1 00:00)
 *   Day N+1 · 01:00  →  Day N+1 00:00 value  +  kWh delivered 00:00 → 01:00 of Day N+1
 *   … and so on — the odometer never resets; each new day continues from where the
 *   previous day left off.
 *
 * Implementation:
 *   1. Find the earliest date in the full dataset ("epoch day").
 *   2. Compute hourly-slot energy for every day from epoch to the selected date.
 *   3. The kwhAcc for slot H on the selected date is:
 *        Σ (all daily totals for days before selected date)  +  kWh[0..H] on selected date
 *
 * This keeps the carry-over accurate even when the user is not viewing Day 1.
 */
function buildHourlyRows(rows: TelemetryRow[], dateStr: string): HourlyRow[] {
  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  /* ── 1. Unique sorted list of all dates in the dataset ─────────────────── */
  const allDates = [...new Set(sorted.map(r => r.date))].sort();
  const selectedIdx = allDates.indexOf(dateStr);

  /* ── 2. Helper: compute 24-slot cumulative kWh for a single calendar day ── */
  //
  // slot[h] = kWh delivered from 00:00 (midnight) of `date` through end of
  // hour h.  slot[0] is ALWAYS forced to 0 — at the stroke of midnight no
  // energy has accumulated yet for the new day.
  //
  // The bridge row (last sample of the previous day) is still included in the
  // trapezoidal integration so the tiny sliver between midnight and the first
  // real sample of the day is captured accurately.  We then subtract that
  // baseline so every slot is anchored to zero at midnight.
  function dailyHourSlots(date: string, integrate: (rows: TelemetryRow[]) => number): number[] {
    const dayRows = sorted.filter(r => r.date === date);

    const prevDate = (() => {
      const d = new Date(date + 'T00:00:00');
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    })();
    const prevRows  = sorted.filter(r => r.date === prevDate);
    const bridgeRow = prevRows.length ? [prevRows[prevRows.length - 1]] : [];

    // baseline = energy the integration "already has" from the bridge row
    // before any sample of the current day is included.  With only 1 row
    // the integrators return 0, so baseline is 0 when there is no bridge.
    const baseline = integrate([...bridgeRow]);

    const slots: number[] = Array(24).fill(0);
    for (let h = 0; h < 24; h++) {
      const slice = dayRows.filter(r => r.timestamp.getHours() <= h);
      slots[h] = Math.max(0, integrate([...bridgeRow, ...slice]) - baseline);
    }
    // slot[0] retains the energy actually integrated during hour 0 (00:00–00:59).
    // Removing the hard-zero anchor ensures the rolling odometer at 00:00 of the
    // next day equals the previous day's last accumulated value plus the energy
    // delivered during hour 0 of the new day.
    return slots;
  }

  /* ── 3. Sum of full-day totals for every day *before* the selected date     */
  /*    but within the SAME calendar month — the accumulator resets to 0 on   */
  /*    the 1st of every month.                                                */
  const selectedMonth = dateStr.slice(0, 7); // "YYYY-MM"
  let carryKwh  = 0;
  let carryKvah = 0;
  if (selectedIdx > 0) {
    for (const prevDate of allDates.slice(0, selectedIdx)) {
      // Only carry days that belong to the same month as the selected date.
      if (prevDate.slice(0, 7) !== selectedMonth) continue;
      carryKwh  += dailyHourSlots(prevDate, energyDelivered)[23];   // entire day's kWh
      carryKvah += dailyHourSlots(prevDate, apparentEnergyKvah)[23]; // entire day's kVAh
    }
  }

  /* ── 4. Hour slots for the selected date ─────────────────────────────────── */
  const todaySlotsKwh  = dailyHourSlots(dateStr, energyDelivered);
  const todaySlotsKvah = dailyHourSlots(dateStr, apparentEnergyKvah);
  const dayRows        = sorted.filter(r => r.date === dateStr);

  return Array.from({ length: 24 }, (_, h) => {
    const hr      = dayRows.filter(r => r.timestamp.getHours() === h);
    const pfValid = hr.filter(isValidPfReading).map(r => powerFactor(r)).filter(v => v > 0);

    // Accumulated value displayed at row h = total BEFORE hour h starts.
    // This way: Acc[h] + ThisHour[h] = Acc[h+1], which is what the user expects.
    // For h=0: nothing from today has run yet, so just the month carry.
    // For h>0: carry + slots[h-1] (everything up to end of previous hour).
    const kwhAcc  = h === 0 ? carryKwh  : carryKwh  + todaySlotsKwh[h - 1];
    const kvahAcc = h === 0 ? carryKvah : carryKvah + todaySlotsKvah[h - 1];

    // Energy delivered within this specific hour slot (true per-hour delta).
    const kwhDelta = h === 0
      ? todaySlotsKwh[0]
      : Math.max(0, todaySlotsKwh[h] - todaySlotsKwh[h - 1]);
    const kvahDelta = h === 0
      ? todaySlotsKvah[0]
      : Math.max(0, todaySlotsKvah[h] - todaySlotsKvah[h - 1]);

    return {
      hour:      h,
      kwhAcc,
      kvahAcc,
      kwhDelta,
      kvahDelta,
      pfAvg:     mean(pfValid),
      count:     hr.length,
    };
  });
}

function mean(arr: number[]): number {
  if (!arr.length) return 0;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

function hh(h: number): string { return String(h).padStart(2, '0') + ':00'; }
function f2(n: number): string { return Number.isFinite(n) && n >= 0 ? n.toFixed(2) : '—'; }
function f3(n: number): string { return n > 0 && Number.isFinite(n) ? n.toFixed(3) : '—'; }

/* -------------------------------------------------------------------------- */
/*  Styles — compact to fit 24 rows + summary + footer on one A4 page        */
/* -------------------------------------------------------------------------- */

const C = {
  black:   '#000000',
  header:  '#1a1a2e',
  rowAlt:  '#f5f5f5',
  rowBord: '#cccccc',
  warn:    '#c0392b',
  ok:      '#27ae60',
  accent:  '#1565C0',
  white:   '#ffffff',
  light:   '#888888',
};

const S = StyleSheet.create({
  page: {
    fontFamily:        'Helvetica',
    fontSize:          9,
    color:             C.black,
    paddingHorizontal: 20,
    paddingTop:        20,
    paddingBottom:     18,
    backgroundColor:   C.white,
  },
  companyRow:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 3 },
  companyName: { fontSize: 15, fontFamily: 'Helvetica-Bold', color: C.header },
  companyTag:  { fontSize: 8,  color: C.light, marginTop: 1 },
  docTitle:    { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.accent, textAlign: 'right' },
  docSub:      { fontSize: 8,  color: C.light, textAlign: 'right', marginTop: 1 },
  divider:     { borderBottomWidth: 1.5, borderBottomColor: C.header, marginVertical: 4 },
  infoRow:     { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 7 },
  infoBox:     { borderWidth: 1, borderColor: C.rowBord, borderRadius: 3, paddingHorizontal: 8, paddingVertical: 4, minWidth: 100 },
  infoLabel:   { fontSize: 7, color: C.light, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', marginBottom: 1 },
  infoVal:     { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.header },
  tableHead:   { flexDirection: 'row', backgroundColor: C.header, paddingVertical: 5, paddingHorizontal: 4, borderTopLeftRadius: 2, borderTopRightRadius: 2 },
  thText:      { fontFamily: 'Helvetica-Bold', fontSize: 8, color: C.white, textAlign: 'center' },
  tableRow:    { flexDirection: 'row', paddingVertical: 3.5, paddingHorizontal: 4, borderBottomWidth: 0.5, borderBottomColor: C.rowBord },
  tableRowAlt: { flexDirection: 'row', paddingVertical: 3.5, paddingHorizontal: 4, borderBottomWidth: 0.5, borderBottomColor: C.rowBord, backgroundColor: C.rowAlt },
  tdText:  { fontSize: 9, textAlign: 'center', color: C.black },
  tdBold:  { fontSize: 9, textAlign: 'center', color: C.black, fontFamily: 'Helvetica-Bold' },
  tdWarn:  { fontSize: 9, textAlign: 'center', color: C.warn,  fontFamily: 'Helvetica-Bold' },
  tdOk:    { fontSize: 9, textAlign: 'center', color: C.ok },
  sumRow:    { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 4, backgroundColor: '#e8f4fd', borderTopWidth: 1.5, borderTopColor: C.accent },
  sumLabel:  { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.accent },
  sumVal:    { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.accent, textAlign: 'center' },
  /* Footer — Generated left, two signature boxes right */
  footer:     { marginTop: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' },
  footerLeft: { flexDirection: 'column' },
  footerText: { fontSize: 8, color: '#333333' },
  signBlock:  { alignItems: 'center', minWidth: 130 },
  signLine:   { borderTopWidth: 1, borderTopColor: C.black, width: 130, marginBottom: 3 },
  signLabel:  { fontSize: 7.5, color: C.light },
});

// Column order: HOUR | kWh Acc | kWh This Hour | kVAh Acc | kVAh This Hour | PF
const W = { hr: '12%', kwhAcc: '18%', kwhThis: '16%', kvahAcc: '18%', kvahThis: '16%', pf: '20%' };

/* -------------------------------------------------------------------------- */
/*  PDF Document                                                               */
/* -------------------------------------------------------------------------- */

function HourlyLogDocument({
  rows, dateStr, siteName, meterName, generatedAt,
}: {
  rows:        HourlyRow[];
  dateStr:     string;
  siteName:    string;
  meterName:   string;
  generatedAt: string;
}) {
  const dataRows    = rows.filter(r => r.count > 0);
  // kwhToday/kvahToday = sum of per-hour deltas for this day only (not the rolling carry-over)
  const kwhToday    = dataRows.reduce((s, r) => s + r.kwhDelta,  0);
  const kvahToday   = dataRows.reduce((s, r) => s + r.kvahDelta, 0);
  // End-of-day rolling total = last data row's Acc (= value before that hour) + that hour's delta
  // This equals carry + slots[last hour], i.e. everything up through end of the last data hour.
  const lastRow     = dataRows.length ? dataRows[dataRows.length - 1] : null;
  const kwhRolling  = lastRow ? lastRow.kwhAcc  + lastRow.kwhDelta  : 0;
  const kvahRolling = lastRow ? lastRow.kvahAcc + lastRow.kvahDelta : 0;
  //const pfOverall   = mean(dataRows.filter(r => r.pfAvg > 0).map(r => r.pfAvg));
  const hasData     = dataRows.length > 0;

  // PF for the End-of-Day summary = kWhRolling / kVAhRolling
  const pfEndOfDay  = kvahRolling > 0 ? kwhRolling / kvahRolling : 0;

  return (
    <Document title={`Power Log · ${dateStr} · ${siteName}`} author="VIREON Cortex">
      <Page size="A4" style={S.page}>

        {/* Header */}
        <View style={S.companyRow}>
          <View>
            <Text style={S.companyName}>{siteName}</Text>
            <Text style={S.companyTag}>{siteName} · {meterName}</Text>
          </View>
          <View>
            <Text style={S.docTitle}>HOURLY POWER LOG</Text>
            <Text style={S.docSub}>Date: {dateStr}</Text>
          </View>
        </View>
        <View style={S.divider} />

        {/* Info strip */}
        <View style={S.infoRow}>
          <View style={S.infoBox}>
            <Text style={S.infoLabel}>Date</Text>
            <Text style={S.infoVal}>{dateStr}</Text>
          </View>
          <View style={S.infoBox}>
            <Text style={S.infoLabel}>kWh Today</Text>
            <Text style={S.infoVal}>{hasData ? `${kwhToday.toFixed(2)} kWh` : '—'}</Text>
          </View>
          <View style={S.infoBox}>
            <Text style={S.infoLabel}>kVAh Today</Text>
            <Text style={S.infoVal}>{hasData ? `${kvahToday.toFixed(2)} kVAh` : '—'}</Text>
          </View>
          <View style={S.infoBox}>
            <Text style={S.infoLabel}>Overall Power Factor</Text>
            <Text style={[S.infoVal, { color: pfEndOfDay > 0 && pfEndOfDay < 0.92 ? C.warn : C.ok }]}>
              {pfEndOfDay > 0 ? pfEndOfDay.toFixed(3) : '—'}
            </Text>
          </View>
          <View style={S.infoBox}>
            <Text style={S.infoLabel}>Samples</Text>
            <Text style={S.infoVal}>{dataRows.reduce((s, r) => s + r.count, 0)}</Text>
          </View>
        </View>

        {/* Table header — order: HOUR | kWh Acc | kWh This Hour | kVAh Acc | kVAh This Hour | PF */}
        <View style={S.tableHead}>
          <Text style={[S.thText, { width: W.hr      }]}>HOUR</Text>
          <Text style={[S.thText, { width: W.kwhAcc  }]}>kWh Accumulated</Text>
          <Text style={[S.thText, { width: W.kwhThis }]}>kWh This Hour</Text>
          <Text style={[S.thText, { width: W.kvahAcc }]}>kVAh Accumulated</Text>
          <Text style={[S.thText, { width: W.kvahThis}]}>kVAh This Hour</Text>
          <Text style={[S.thText, { width: W.pf      }]}>Power Factor (kWh/kVAh)</Text>
        </View>

        {/* Data rows */}
        {(() => {
          // Track last known accumulated values to carry forward during no-data hours
          let lastKwhAcc  = 0;
          let lastKvahAcc = 0;

          return rows.map((r, i) => {
            const rowStyle = i % 2 === 0 ? S.tableRowAlt : S.tableRow;
            const noData   = r.count === 0;

            // Carry forward the last known accumulated value when there is no data.
            // Store acc+delta (= value AFTER that hour completed) so the carried
            // value shown on no-data rows is the correct end-of-last-known-hour total.
            if (!noData) {
              lastKwhAcc  = r.kwhAcc  + r.kwhDelta;
              lastKvahAcc = r.kvahAcc + r.kvahDelta;
            }
            const displayKwhAcc  = noData ? lastKwhAcc  : r.kwhAcc;
            const displayKvahAcc = noData ? lastKvahAcc : r.kvahAcc;

            // PF for this row = kWh Accumulated / kVAh Accumulated
            const rowPf = displayKvahAcc > 0 ? displayKwhAcc / displayKvahAcc : 0;
            const pfBad = rowPf > 0 && rowPf < 0.92;

            // "This Hour" = the energy consumed DURING this clock-hour.
            // r.kwhDelta is exactly that: slots[h] - slots[h-1].
            // No shift: row H shows its own delta, not the next row's.
            const kwhThisHour  = noData ? 0 : r.kwhDelta;
            const kvahThisHour = noData ? 0 : r.kvahDelta;
            const showThisHour = !noData;

            return (
              <View key={r.hour} style={rowStyle}>
                <Text style={[S.tdBold, { width: W.hr }]}>{hh(r.hour)}</Text>
                {/* kWh Accumulated — always show last known value, never — */}
                <Text style={[S.tdBold, { width: W.kwhAcc }]}>
                  {displayKwhAcc > 0 ? f2(displayKwhAcc) : '—'}
                </Text>
                {/* kWh This Hour */}
                <Text style={[S.tdText, { width: W.kwhThis }]}>
                  {showThisHour ? f2(kwhThisHour) : '—'}
                </Text>
                {/* kVAh Accumulated — always show last known value, never — */}
                <Text style={[S.tdBold, { width: W.kvahAcc }]}>
                  {displayKvahAcc > 0 ? f2(displayKvahAcc) : '—'}
                </Text>
                {/* kVAh This Hour */}
                <Text style={[S.tdText, { width: W.kvahThis }]}>
                  {showThisHour ? f2(kvahThisHour) : '—'}
                </Text>
                {/* Power Factor = kWh Accumulated / kVAh Accumulated */}
                <Text style={[pfBad ? S.tdWarn : S.tdOk, { width: W.pf }]}>
                  {rowPf > 0 ? f3(rowPf) : '—'}
                </Text>
              </View>
            );
          });
        })()}

        {/* Summary — Day Total */}
        {hasData && (
          <View style={S.sumRow}>
            <Text style={[S.sumLabel, { width: W.hr      }]}>End of Day</Text>
            <Text style={[S.sumVal,   { width: W.kwhAcc  }]}>{f2(kwhRolling)} kWh</Text>
            <Text style={[S.sumVal,   { width: W.kwhThis }]}>{f2(kwhToday)} kWh</Text>
            <Text style={[S.sumVal,   { width: W.kvahAcc }]}>{f2(kvahRolling)} kVAh</Text>
            <Text style={[S.sumVal,   { width: W.kvahThis}]}>{f2(kvahToday)} kVAh</Text>
            <Text style={[S.sumVal,   { width: W.pf      }]}>{pfEndOfDay > 0 ? pfEndOfDay.toFixed(3) : '—'}</Text>
          </View>
        )}

        {/* Footer — Generated timestamp left, two signature boxes right */}
        <View style={S.footer}>
          <View style={S.footerLeft}>
            <Text style={S.footerText}>Generated: {generatedAt}</Text>
            <Text style={S.footerText}>VIREON Cortex</Text>
          </View>
          <View style={S.signBlock}>
            <View style={S.signLine} />
            <Text style={S.signLabel}>Operator Signature</Text>
          </View>
          <View style={S.signBlock}>
            <View style={S.signLine} />
            <Text style={S.signLabel}>Supervisor Signature</Text>
          </View>
        </View>

      </Page>
    </Document>
  );
}

/* -------------------------------------------------------------------------- */
/*  Card component                                                             */
/* -------------------------------------------------------------------------- */

export function HourlyLogPdfCard({
  meterRows,
  meterName = 'Meter',
  siteName  = 'Site',
}: {
  meterRows:  TelemetryRow[];
  meterName?: string;
  siteName?:  string;
}) {
  const defaultDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const [selectedDate, setSelectedDate] = useState(defaultDate);

  const hourlyRows = useMemo(
    () => buildHourlyRows(meterRows, selectedDate),
    [meterRows, selectedDate],
  );

  const hasData     = hourlyRows.some(r => r.count > 0);
  const generatedAt = format(new Date(), 'dd-MMM-yyyy HH:mm');
  const fileName    = `power-log-${selectedDate}.pdf`;
  const minDate     = meterRows.length ? meterRows[0].date                    : undefined;
  const maxDate     = meterRows.length ? meterRows[meterRows.length - 1].date : undefined;

  return (
    <Card>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">
            Hourly Power Log · PDF
          </p>
          <p className="text-xs text-ink-500">
            Download a printable daily log · Accumulated kWh, Power Factor per hour
          </p>
        </div>
        <FileDown className="w-5 h-5 text-ink-400 shrink-0 mt-0.5" />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <CalendarDays className="w-4 h-4 text-ink-500 shrink-0" />
        <input
          type="date"
          value={selectedDate}
          min={minDate}
          max={maxDate}
          onChange={e => setSelectedDate(e.target.value)}
          className="flex-1 rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm font-semibold text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
      </div>

      {hasData ? (
        <div className="mb-4 rounded-xl bg-brand-50 border border-brand-100 px-3 py-2 text-xs text-brand-800 font-semibold">
          {selectedDate} · {hourlyRows.filter(r => r.count > 0).length} of 24 hours have data
        </div>
      ) : selectedDate ? (
        <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 font-semibold">
          No data for {selectedDate}
        </div>
      ) : null}

      <PDFDownloadLink
        document={
          <HourlyLogDocument
            rows={hourlyRows}
            dateStr={selectedDate}
            siteName={siteName}
            meterName={meterName}
            generatedAt={generatedAt}
          />
        }
        fileName={fileName}
      >
        {({ loading }) => (
          <button
            type="button"
            disabled={loading || !hasData}
            className={[
              'w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3.5',
              'text-sm font-bold transition-all duration-200 active:scale-95',
              loading || !hasData
                ? 'bg-ink-100 text-ink-400 cursor-not-allowed'
                : 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm',
            ].join(' ')}
          >
            <FileDown className="w-4 h-4" />
            {loading ? 'Building PDF…' : !hasData ? 'No data for this day' : `Download Log · ${selectedDate}`}
          </button>
        )}
      </PDFDownloadLink>
    </Card>
  );
}